import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { cn } from "@/utils/core/generalUtils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground active:scale-95 hover:opacity-95 transition-all",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90  active:scale-95 transition-all",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground active:scale-95 transition-all",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:scale-95 transition-all",
        ghost:
          "hover:bg-accent hover:text-accent-foreground active:scale-95 transition-all",
        link: "text-primary underline-offset-4 hover:underline active:scale-95 transition-all",
        light:
          "bg-primaryLight text-primaryLight-foreground hover:opacity-90 active:scale-95 transition-all",
        success:
          "bg-[#28AE60] text-primary-foreground hover:opacity-90 active:scale-95 transition-all",
        lightDestructive:
          "border-transparent bg-[#FEEBEB] text-[#FF5B5B] hover:opacity-90  transition-all",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  message?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      children,
      isLoading,
      message,
      variant,
      size,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        className={cn(
          "dark:text-white ",
          buttonVariants({ variant, size, className }),
        )}
        ref={ref}
        disabled={isLoading}
        type='button'
        {...props}
      >
        {isLoading ? (
          <span className='flex items-center gap-3'>
            {isLoading && <Loader2 className='animate-spin' />}
            {message && <span>{message}</span>}
          </span>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
